import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Upload, Download, ChevronDown } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// RowExportMenu — one-tap export dropdown that lives on every History row
// (parent + child sessions). Uses a React Portal so the menu escapes any
// `overflow: hidden` container and floats above the row / cockpit chrome.
//
// Renders a compact "Export" button with a chevron. Clicking opens a menu
// with three destinations wired to the SAME `onExport(workout, platform)`
// handler that the expanded row uses. Platforms:
//   - `fit`        — Download TCX (works offline, always enabled)
//   - `strava`     — Requires Strava OAuth connect (from Settings)
//   - `intervals`  — Requires Intervals.icu API key (from Settings)
//
// Child sessions inside SessionBreakdown re-use this same component and
// pass the PARENT workout — the TCX file is always the full session,
// which matches how Strava / Intervals both prefer whole-workout uploads.
// ─────────────────────────────────────────────────────────────────────────────

function PortalMenu({ anchorRef, open, onClose, minWidth = 220, children }) {
  const menuRef = useRef(null);
  const [style, setStyle] = useState({ top: 0, left: 0, visibility: 'hidden' });

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) return;
    const compute = () => {
      const rect = anchorRef.current.getBoundingClientRect();
      const menuH = menuRef.current?.offsetHeight || 200;
      const menuW = menuRef.current?.offsetWidth || minWidth;
      const vpH = window.innerHeight;
      const vpW = window.innerWidth;
      const spaceBelow = vpH - rect.bottom;
      const openUp = spaceBelow < menuH + 12 && rect.top > menuH + 12;
      let top = openUp ? rect.top - menuH - 8 : rect.bottom + 8;
      // Right-align to the trigger so the menu doesn't push off-screen
      // for rows near the right edge of the viewport.
      let left = rect.right - menuW;
      left = Math.max(8, Math.min(left, vpW - menuW - 8));
      top = Math.max(8, Math.min(top, vpH - menuH - 8));
      setStyle({ top, left, visibility: 'visible' });
    };
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [open, anchorRef, minWidth]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) &&
          anchorRef.current && !anchorRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;
  return createPortal(
    <div
      ref={menuRef}
      className="bg-white dark:bg-zinc-800 border-2 border-zinc-200 dark:border-zinc-700 rounded-xl shadow-xl overflow-hidden"
      style={{ position: 'fixed', minWidth, zIndex: 9999, ...style }}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      data-testid="row-export-portal-menu"
    >
      {children}
    </div>,
    document.body
  );
}

const StravaGlyph = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#FC4C02" aria-hidden="true">
    <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.599h4.172L10.463 0l-7 13.828h4.169" />
  </svg>
);

const IntervalsGlyph = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="4" y="8" width="3" height="10" rx="1" fill="#8B5CF6" />
    <rect x="10" y="4" width="3" height="14" rx="1" fill="#A78BFA" />
    <rect x="16" y="6" width="3" height="12" rx="1" fill="#C4B5FD" />
  </svg>
);

/**
 * @param {object} props
 * @param {object} props.workout            — the workout doc to export
 * @param {boolean} props.stravaConnected   — user has Strava OAuth linked
 * @param {boolean} props.intervalsConnected — user has Intervals API key
 * @param {Function} props.onExport         — (workout, platform) → void
 * @param {string} [props.testidPrefix]     — e.g. "row-export" or "child-export"
 * @param {'sm'|'md'} [props.size]          — visual density
 * @param {'button'|'icon'} [props.variant] — icon-only for tight spots (child rows)
 */
export default function RowExportMenu({
  workout,
  stravaConnected = false,
  intervalsConnected = false,
  onExport,
  testidPrefix = 'row-export',
  size = 'sm',
  variant = 'button',
}) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const workoutId = workout?.workout_id || workout?.parent_workout_id || 'unknown';
  const testid = `${testidPrefix}-${workoutId}`;

  const handlePick = (platform) => (e) => {
    e.stopPropagation();
    setOpen(false);
    try {
      onExport?.(workout, platform);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[RowExportMenu] onExport threw:', err);
    }
  };

  const iconOnly = variant === 'icon';
  const btnClass = iconOnly
    ? 'p-2 rounded-lg border-2 border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-colors flex items-center justify-center'
    : `${size === 'sm' ? 'px-2.5 py-1.5 text-xs' : 'px-3 py-2 text-sm'} rounded-lg border-2 border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-colors flex items-center gap-1.5 font-bold uppercase tracking-wider whitespace-nowrap`;

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onPointerDown={(e) => { e.stopPropagation(); }}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className={btnClass}
        data-testid={testid}
        aria-label="Export session"
        aria-haspopup="menu"
        aria-expanded={open}
        title="Export session"
      >
        <Upload className={iconOnly ? 'w-4 h-4' : 'w-3.5 h-3.5'} />
        {!iconOnly && <span>Export</span>}
        {!iconOnly && <ChevronDown className="w-3 h-3" />}
      </button>

      <PortalMenu anchorRef={anchorRef} open={open} onClose={() => setOpen(false)} minWidth={240}>
        <div className="py-1">
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={handlePick('fit')}
            className="w-full px-3 py-2.5 flex items-center gap-3 hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-colors text-left"
            data-testid={`${testid}-tcx`}
          >
            <div className="w-8 h-8 bg-zinc-100 dark:bg-zinc-700 rounded-lg flex items-center justify-center">
              <Download className="w-4 h-4 text-zinc-700 dark:text-zinc-200" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-bold text-zinc-800 dark:text-zinc-100">Download TCX</div>
              <div className="text-[11px] text-zinc-400">Save the file to your device</div>
            </div>
          </button>

          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={handlePick('strava')}
            className="w-full px-3 py-2.5 flex items-center gap-3 hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-colors text-left"
            data-testid={`${testid}-strava`}
          >
            <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
              <StravaGlyph />
            </div>
            <div className="flex-1">
              <div className="text-sm font-bold text-orange-600 dark:text-orange-300">Strava</div>
              <div className="text-[11px] text-zinc-400">
                {stravaConnected ? 'Upload to Strava' : 'Connect in Settings first'}
              </div>
            </div>
            {stravaConnected && <span className="w-2 h-2 bg-green-500 rounded-full" aria-hidden="true" />}
          </button>

          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={handlePick('intervals')}
            className="w-full px-3 py-2.5 flex items-center gap-3 hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-colors text-left"
            data-testid={`${testid}-intervals`}
          >
            <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
              <IntervalsGlyph />
            </div>
            <div className="flex-1">
              <div className="text-sm font-bold text-purple-600 dark:text-purple-300">Intervals.icu</div>
              <div className="text-[11px] text-zinc-400">
                {intervalsConnected ? 'Upload to Intervals.icu' : 'Add API key in Settings'}
              </div>
            </div>
            {intervalsConnected && <span className="w-2 h-2 bg-green-500 rounded-full" aria-hidden="true" />}
          </button>
        </div>
      </PortalMenu>
    </>
  );
}
